import { createContext, useContext, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';

export type FeatureFlag = 'ai_agent' | 'lead_qualification';

interface FeaturesContextType {
  flags: Record<string, boolean>;
  isEnabled: (flag: FeatureFlag) => boolean;
}

const FeaturesContext = createContext<FeaturesContextType>({
  flags: {},
  isEnabled: () => false,
});

interface FeaturesProviderProps {
  children: ReactNode;
}

export const FeaturesProvider: React.FC<FeaturesProviderProps> = ({ children }) => {
  const { user } = useAuth();

  const value = useMemo<FeaturesContextType>(() => {
    const flags = user?.features ?? {};
    return {
      flags,
      isEnabled: (flag: FeatureFlag) => !!flags[flag],
    };
  }, [user?.features]);

  return <FeaturesContext.Provider value={value}>{children}</FeaturesContext.Provider>;
};

export const useFeatures = () => useContext(FeaturesContext);

export const useFeatureFlag = (flag: FeatureFlag): boolean => {
  const { isEnabled } = useFeatures();
  return isEnabled(flag);
};
